import { BROWSER_STORAGE_KEYS, PING_INTERVALS, QUERY_KEYS } from '@constants';
import { useQuery } from 'react-query';
import { fetchClientByLeadId } from '@services';
import { abbreviations } from '@utils/i18n';
import { setClientConnected, setRepConnected, setLanguage } from '@actions/creators';
import { getErrorMessage } from '@utils/errorHandling';
import { setBrowserStorageItem } from '@utils/browserStorage';

/* Custom hook wrappers around react query queries */

const { clientQuery } = QUERY_KEYS;
const { threeSeconds, oneMinute, fiveMinutes } = PING_INTERVALS;
const { clientConnectionKey } = BROWSER_STORAGE_KEYS;

const changeLanguage = (i18n, client) => {
    const { language } = client;

    i18n.changeLanguage(abbreviations[language]);
};

const setConnectionStatus = (client, dispatch, connectionType, action) => {
    const value = client[connectionType];

    if (client.hasOwnProperty(connectionType)) {
        dispatch(action(value));

        if (connectionType === 'clientConnected') {
            if (value === undefined) {
                console.warn('clientConnected is undefined in client query');
            } else {
                setBrowserStorageItem(clientConnectionKey, value);
            }
        }
    } else {
        console.warn(`Client did not have ${connectionType} property; value was ${value}`);
    }
};

export const usePdfPageClientQuery = ({ leadId }, i18n, setUnauthorized) =>
    useQuery([clientQuery, leadId], () => fetchClientByLeadId({ leadId }), 
    {
        onSuccess: client => {
            if (!client) {
                setUnauthorized(true);
            } else {
                changeLanguage(i18n, client);
            }
        }
    }
);

export const useRepClientQuery = ({ leadId, profile }, queryData) =>  
    useQuery([clientQuery, leadId, profile], () => fetchClientByLeadId({ leadId, profile }), 
    {
        onSuccess: client => {
            const { language } = client;
            const { dispatch, initialLanguageLoaded, setInitialLanguageLoaded } = queryData;
            
            setConnectionStatus(client, dispatch, 'clientConnected', setClientConnected);

            if (!initialLanguageLoaded) {
                dispatch(setLanguage(language));
                setInitialLanguageLoaded(true);
            }
        },
       refetchInterval: threeSeconds  
    }
);

export const useClientQuery = ({ leadId }, queryData) =>
    useQuery([clientQuery, leadId], () => fetchClientByLeadId({ leadId }), {
        onSuccess: client => {
            const { dispatch, setDisplayWaitingMessage, initialLanguageLoaded, setInitialLanguageLoaded } = queryData;
            // As a semi-workaround to protect client data when rep isn't connected, we send an empty response when the rep isn't connected.
            // In that case, just render the waiting message
            if (!client) {
                setDisplayWaitingMessage(true);
                return;
            }

            const { language } = client;
            
            setDisplayWaitingMessage(false);

            if (!initialLanguageLoaded) {
                dispatch(setLanguage(language));
                setInitialLanguageLoaded(true);
            }

            setConnectionStatus(client, dispatch, 'repConnected', setRepConnected);
        },
        onError: err => {
            const { setErrorMsg, setRefetchInterval, setDisplayWaitingMessage } = queryData;
            const responseMessage = err.response?.data;
            // Some errors that we want to handle in a more controlled way 
            const clientNotInDatabase = `Could not find client ${leadId} with associated credit analysis`;
            const invalidLeadId = 'LeadId must be a number. Received NaN';
            
            // Avoid refetching when we know the error won't change
            if (responseMessage === clientNotInDatabase) {
                setDisplayWaitingMessage(true);
                setRefetchInterval(fiveMinutes);
            } else if (responseMessage === invalidLeadId) {
                setErrorMsg('Invalid link. Please double check the URL with your financial consultant.');
                setRefetchInterval(fiveMinutes);
            } else {
                // Update refetching to once a minute for unhandled/server errors 
                setRefetchInterval(oneMinute);
                setErrorMsg(`${getErrorMessage(err)} - Please refresh the page.`);
            }
        },
        retry: (failureCount, error) => {
            const status = error.response?.status;
            const handledErrors = [400, 404];

            if (status && handledErrors.includes(status)) {
                return false;
            }
        },
        refetchInterval: queryData.refetchInterval,        
        refetchIntervalInBackground: true,
        keepPreviousData: true
    }
);
